import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe2, Sun, Moon, ChevronDown, Menu, X } from "lucide-react";
import { useTheme } from "../lib/theme";
import { PRODUCT_ORDER, PRODUCTS } from "../lib/products";
import { analytics } from "../lib/analytics";

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_f69fe3c2-391a-4258-b806-47ff98915422/artifacts/gv4jpbei_logo_white_big.png";
const LOGO_BLACK = "https://customer-assets.emergentagent.com/job_f69fe3c2-391a-4258-b806-47ff98915422/artifacts/7bnwo8i7_logo_dark.png";

const LANGS = [
  { code: "en", label: "EN", name: "English" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "es", label: "ES", name: "Español" },
  { code: "ar", label: "AR", name: "العربية" },
];

const Header = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const megaTimer = useRef(null);
  const lastYRef = useRef(0);
  const openMega = () => {
    if (megaTimer.current) clearTimeout(megaTimer.current);
    if (!megaOpen) analytics.megaMenuOpen();
    setMegaOpen(true);
  };
  const closeMega = () => {
    if (megaTimer.current) clearTimeout(megaTimer.current);
    megaTimer.current = setTimeout(() => setMegaOpen(false), 180);
  };
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      // Hide when scrolling DOWN past 120px, reveal on any upward scroll
      const last = lastYRef.current;
      const delta = y - last;
      if (Math.abs(delta) > 6) {
        if (delta > 0 && y > 120 && !megaOpen && !menuOpen) {
          setHidden(true);
        } else if (delta < 0) {
          setHidden(false);
        }
        lastYRef.current = y;
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [megaOpen, menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.dir = i18n.dir();
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const changeLang = (code) => {
    if (code !== i18n.language) analytics.languageChange(i18n.language, code);
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const onThemeToggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    analytics.themeToggle(next);
    toggle();
  };

  const logo = theme === "dark" ? LOGO_WHITE : LOGO_BLACK;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 will-change-transform ${
        scrolled ? "py-3" : "py-5"
      } ${hidden ? "-translate-y-full" : "translate-y-0"}`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div
          className={`flex items-center justify-between rounded-full px-4 md:px-6 py-2.5 transition-all ${
            scrolled ? "glass-strong" : "glass-panel"
          }`}
        >
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img src={logo} alt="TRYB International" className="h-7 md:h-8 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 text-sm">
            <div
              className="relative"
              onMouseEnter={openMega}
              onMouseLeave={closeMega}
            >
              <button
                className="flex items-center gap-1.5 rounded-full px-4 py-2 text-foreground/80 hover:text-foreground transition"
                data-testid="header-solutions-menu"
              >
                {t("nav.solutions")}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {megaOpen && (
                <div
                  className="absolute start-1/2 top-full -translate-x-1/2 pt-3 w-[640px]"
                  data-testid="header-solutions-mega"
                  onMouseEnter={openMega}
                  onMouseLeave={closeMega}
                >
                  <div className="rounded-2xl glass-strong p-4 grid grid-cols-2 gap-2">
                  {PRODUCT_ORDER.map((slug) => {
                    const p = PRODUCTS[slug];
                    const Icon = p.icon;
                    return (
                      <Link
                        key={slug}
                        to={`/${slug}`}
                        className="group flex items-start gap-3 rounded-xl p-3 hover:bg-foreground/5 transition"
                        data-testid={`header-mega-${slug}`}
                      >
                        <div
                          className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                          style={{
                            background: `${p.accentColor}22`,
                            boxShadow: `inset 0 0 0 1px ${p.accentColor}55`,
                          }}
                        >
                          <Icon className="h-5 w-5" style={{ color: p.accentColor }} strokeWidth={1.8} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold">{p.name}</p>
                            {p.status === "coming_soon" && (
                              <span
                                className="text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5"
                                style={{ background: `${p.accentColor}22`, color: p.accentColor }}
                              >
                                soon
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">{t(`products.${slug}.tagline`)}</p>
                        </div>
                      </Link>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/#about" className="rounded-full px-4 py-2 text-foreground/80 hover:text-foreground transition">
              {t("nav.about")}
            </NavLink>
            <NavLink to="/#contact" className="rounded-full px-4 py-2 text-foreground/80 hover:text-foreground transition">
              {t("nav.contact")}
            </NavLink>
          </nav>

          <div className="flex items-center gap-2">
            {/* Language */}
            <div className="relative">
              <button
                onClick={() => setLangOpen((o) => !o)}
                className="flex items-center gap-1 rounded-full px-3 py-2 text-xs font-semibold text-foreground/80 hover:text-foreground hover:bg-foreground/5 transition"
                data-testid="header-language-button"
                aria-label="Language"
              >
                <Globe2 className="h-4 w-4" />
                <span>{i18n.language.toUpperCase().slice(0, 2)}</span>
              </button>
              {langOpen && (
                <div
                  className="absolute end-0 mt-2 w-44 rounded-xl glass-strong p-1 z-50"
                  data-testid="header-language-dropdown"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => changeLang(l.code)}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-foreground/5 transition ${
                        i18n.language === l.code ? "text-foreground" : "text-muted-foreground"
                      }`}
                      data-testid={`header-lang-${l.code}`}
                    >
                      <span>{l.name}</span>
                      <span className="text-xs">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={onThemeToggle}
              className="hidden sm:flex items-center justify-center h-9 w-9 rounded-full text-foreground/80 hover:text-foreground hover:bg-foreground/5 transition"
              aria-label="Toggle theme"
              data-testid="header-theme-toggle"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <Link to="/#contact" className="hidden md:inline-flex btn-primary !py-2 !px-5 !text-xs" data-testid="header-cta">
              {t("nav.getStarted")}
            </Link>

            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="lg:hidden h-9 w-9 flex items-center justify-center rounded-full hover:bg-foreground/5 transition"
              aria-label="Menu"
              data-testid="header-mobile-toggle"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden mt-2 rounded-2xl glass-strong p-4 space-y-2" data-testid="header-mobile-menu">
            <p className="px-3 pt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t("nav.solutions")}
            </p>
            {PRODUCT_ORDER.map((slug) => {
              const p = PRODUCTS[slug];
              return (
                <Link
                  key={slug}
                  to={`/${slug}`}
                  className="block rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-foreground/5"
                  data-testid={`mobile-link-${slug}`}
                >
                  {p.name}
                </Link>
              );
            })}
            <div className="border-t border-foreground/10 pt-3 mt-3 space-y-1">
              <Link to="/#about" className="block rounded-xl px-3 py-2.5 text-sm hover:bg-foreground/5">{t("nav.about")}</Link>
              <Link to="/#contact" className="block rounded-xl px-3 py-2.5 text-sm hover:bg-foreground/5">{t("nav.contact")}</Link>
              <button onClick={onThemeToggle} className="w-full text-start flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm hover:bg-foreground/5">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === "dark" ? "Light mode" : "Dark mode"}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
